import React, { useEffect, useCallback, useContext, useState } from "react";
import { useDropzone as useReactDropzone } from "react-dropzone";

import { handleDropAccepted, handleDropRejected } from "core/Dropzone/helpers"; // todo: move these

import Button from "core/Button";

import DropzoneContext from "core/Dropzone/context";

export const useDropzone = ({
    initialMaximumFiles = 10,
    fileTypesAccepted = ["image/jpg", "image/jpeg"],
    minFileSizeBytes = 2621440, // 2.5MB temporary
    maxFileSizeBytes = 52428800 // 50MB temporary
} = {}) => {
    const [maxFiles, setMaxFiles] = useState(initialMaximumFiles);

    const {
        isDisabledDropzone,
        setDisabledDropzone,
        hasAcceptedFiles,
        setAcceptedFiles,
        hasRejectedFiles,
        setRejectedFiles,
        hasRejectedFileErrors,
        setRejectedFileErrors
    } = useContext(DropzoneContext);

    const handleClearErrors = useCallback(() => setRejectedFileErrors([]), []);

    const {
        isDragAccept,
        isDragReject,
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        open
    } = useReactDropzone({
        minSize: minFileSizeBytes,
        maxSize: maxFileSizeBytes,
        disabled: isDisabledDropzone,
        maxFiles: maxFiles,
        accept: fileTypesAccepted,
        onDropAccepted: acceptedFiles => {
            handleDropAccepted({
                acceptedFiles,
                setAcceptedFiles,
                handleClearErrors,
                setMaxFiles,
                maxFiles
            });
        },
        onDropRejected: fileRejections => {
            handleDropRejected({
                fileRejections,
                setRejectedFiles,
                setRejectedFileErrors
            });
        }
    });

    const handleOpenFilePicker = useCallback(
        e => {
            e.preventDefault();
            e.stopPropagation();
            if (hasAcceptedFiles.length >= initialMaximumFiles) {
                return;
            } else {
                handleClearErrors();
                open();
            }
        },
        [open]
    );

    const UploadButton = ({ children, buttonProps }) => {
        return (
            <Button
                {...buttonProps}
                isDisabled={hasAcceptedFiles.length >= initialMaximumFiles}
                onClick={handleOpenFilePicker}
            >
                {children}
            </Button>
        );
    };

    const isDropzoneEmpty = hasAcceptedFiles.length === 0;

    const rejectedFilesCount = hasRejectedFileErrors.length;

    useEffect(() => {
        if (isDragAccept) {
            handleClearErrors();
        }
    }, [isDragAccept]);

    return {
        getRootProps,
        getInputProps,
        isDropzoneEmpty,
        isDragAccept,
        isDragReject,
        initialMaximumFiles,
        maxFiles,
        setMaxFiles,
        minFileSizeBytes,
        maxFileSizeBytes,
        handleOpenFilePicker,
        acceptedFiles,
        hasAcceptedFiles,
        setAcceptedFiles,
        hasRejectedFiles,
        setRejectedFiles,
        isDisabledDropzone,
        setDisabledDropzone,
        handleClearErrors,
        hasRejectedFileErrors,
        UploadButton,
        rejectedFilesCount
    };
};

export const DropzoneProvider = ({ children }) => {
    const [isDisabledDropzone, setDisabledDropzone] = useState(false);
    const [hasAcceptedFiles, setAcceptedFiles] = useState([]);
    const [hasRejectedFiles, setRejectedFiles] = useState([]);
    const [hasRejectedFileErrors, setRejectedFileErrors] = useState([]);

    return (
        <DropzoneContext.Provider
            value={{
                isDisabledDropzone,
                setDisabledDropzone,
                hasAcceptedFiles,
                setAcceptedFiles,
                hasRejectedFiles,
                setRejectedFiles,
                hasRejectedFileErrors,
                setRejectedFileErrors
            }}
        >
            {children}
        </DropzoneContext.Provider>
    );
};
